import { useEffect, useState } from "preact/hooks";
import commerceArtwork from "./assets/hero/commerce-running-shoe.webp";
import servicesArtwork from "./assets/hero/services-rehabilitation.webp";
import supportArtwork from "./assets/hero/support-solar-inverter.webp";
import { Mark } from "./brand";
import {
  ArrowUpRightIcon,
  CheckIcon,
  CloseIcon,
  HumanIcon,
  ShieldIcon,
} from "./icons";

type ScenarioId = "commerce" | "services" | "support";

type Scenario = {
  id: ScenarioId;
  tab: string;
  step: string;
  company: string;
  assistant: string;
  eyebrow: string;
  headline: string;
  description: string;
  firstQuestion: string;
  firstAnswer: string;
  secondQuestion: string;
  finalAnswer: string;
  proof: string;
};

const SCENARIO_DURATION_MS = 15_000;

const scenarios: Scenario[] = [
  {
    id: "commerce",
    tab: "Sklep",
    step: "01",
    company: "PACEFORM",
    assistant: "Doradca PACEFORM",
    eyebrow: "Running / Road collection",
    headline: "Biegnij dalej.\nW swoim tempie.",
    description:
      "Buty dopasowane do nawierzchni, dystansu i Twojego sposobu biegania.",
    firstQuestion: "Szukam butów do biegania do 450 zł.",
    firstAnswer: "Jasne — po jakiej nawierzchni biegasz najczęściej?",
    secondQuestion: "Asfalt, rozmiar 42.",
    finalAnswer:
      "Najlepiej pasuje PACE 001. To model szosowy, dostępny w rozmiarze 42 i w Twoim budżecie.",
    proof: "Katalog · przeznaczenie · warianty",
  },
  {
    id: "services",
    tab: "Usługi",
    step: "02",
    company: "MOTION",
    assistant: "Asystent MOTION",
    eyebrow: "Fizjoterapia / Kraków",
    headline: "Wracaj do ruchu.\nŚwiadomie.",
    description:
      "Diagnostyka i terapia prowadzona przez jeden zespół — od pierwszej wizyty po powrót do sportu.",
    firstQuestion: "Czy pomagacie po rekonstrukcji ACL?",
    firstAnswer:
      "Tak. Prowadzimy terapię od pierwszej oceny aż do bezpiecznego powrotu do sportu.",
    secondQuestion: "Macie termin po 18:00?",
    finalAnswer:
      "Tak — najbliższy termin jest jutro o 18:30. Pierwsza wizyta trwa 60 minut.",
    proof: "Grafik · oferta · czas wizyty",
  },
  {
    id: "support",
    tab: "Support",
    step: "03",
    company: "HELIO",
    assistant: "Support HELIO",
    eyebrow: "Energia / Centrum pomocy",
    headline: "Twoja energia.\nPod kontrolą.",
    description:
      "Instalacje fotowoltaiczne z monitoringiem, opieką techniczną i jasnymi warunkami gwarancji.",
    firstQuestion: "Falownik pokazuje błąd E03. Co mogę zrobić?",
    firstAnswer:
      "Instrukcja zaleca jeden bezpieczny restart i sprawdzenie, czy komunikat znika.",
    secondQuestion: "Restart nie pomógł.",
    finalAnswer:
      "Nie wykonuj kolejnych działań przy urządzeniu. Przekazuję rozmowę technikowi z pełnym kontekstem.",
    proof: "Instrukcja falownika · procedura E03",
  },
];

const artworkByScenario: Record<ScenarioId, string> = {
  commerce: commerceArtwork,
  services: servicesArtwork,
  support: supportArtwork,
};

function SceneArtwork({ scenario }: { scenario: Scenario }) {
  return (
    <div class={`story-art story-art--${scenario.id}`}>
      <div class="story-art__orb" />
      <img
        class="story-art__image"
        src={artworkByScenario[scenario.id]}
        alt=""
        width="1448"
        height="1086"
        decoding="async"
        draggable={false}
      />
      {scenario.id === "commerce" && (
        <div class="story-art__metric">
          <span>PACE / 001</span>
          <strong>359 zł</strong>
        </div>
      )}
      {scenario.id === "services" && (
        <div class="story-art__metric">
          <span>Najbliższy termin</span>
          <strong>Jutro · 18:30</strong>
        </div>
      )}
      {scenario.id === "support" && (
        <div class="story-art__metric">
          <span>Instalacja</span>
          <strong>
            <i /> Online
          </strong>
        </div>
      )}
    </div>
  );
}

function CommerceOutcome() {
  return (
    <div class="story-outcome story-outcome--product">
      <div class="story-product-thumb">
        <img src={commerceArtwork} alt="" />
      </div>
      <div class="story-product-copy">
        <span>Buty szosowe</span>
        <strong>PACE 001 — Piaskowy</strong>
        <small>Rozmiar 42 · dostępny</small>
        <b>359 zł</b>
      </div>
      <ArrowUpRightIcon />
    </div>
  );
}

function ServicesOutcome() {
  return (
    <div class="story-outcome story-outcome--appointment">
      <div class="story-source-icon">
        <CheckIcon />
      </div>
      <div>
        <span>Najbliższy dostępny termin</span>
        <strong>Jutro · 18:30</strong>
        <small>Pierwsza wizyta · 60 min</small>
      </div>
      <ArrowUpRightIcon />
    </div>
  );
}

function SupportOutcome() {
  return (
    <div class="story-outcome story-outcome--handoff">
      <div class="story-handoff-icon">
        <HumanIcon />
      </div>
      <div>
        <strong>Technik otrzymał pełny kontekst</strong>
        <span>Odpowie tutaj — bez powtarzania problemu</span>
      </div>
      <span class="story-handoff-status">
        <i /> Przekazano
      </span>
    </div>
  );
}

function SceneOutcome({ id }: { id: ScenarioId }) {
  if (id === "services") return <ServicesOutcome />;
  if (id === "support") return <SupportOutcome />;
  return <CommerceOutcome />;
}

function TypingIndicator() {
  return (
    <small class="story-typing" aria-hidden="true">
      <i />
      <i />
      <i />
    </small>
  );
}

function AssistantIdentity({ scenario }: { scenario: Scenario }) {
  return (
    <div class="story-author">
      <span>
        <Mark />
      </span>
      <strong>{scenario.assistant}</strong>
    </div>
  );
}

function StoryWidget({ scenario }: { scenario: Scenario }) {
  return (
    <>
      <div class="story-site-mask" />
      <div class="story-focus-ring" />
      <div class="story-cursor" />
      <div class="story-launcher">
        <Mark />
        <span>
          <i /> Zapytaj asystenta
        </span>
      </div>

      <div class="story-widget">
        <header class="story-widget__head">
          <div class="story-widget__identity">
            <div class="story-widget__avatar">
              <Mark />
            </div>
            <div>
              <strong>{scenario.assistant}</strong>
              <span>
                <i /> Asystent online
              </span>
            </div>
          </div>
          <div class="story-widget__close">
            <CloseIcon />
          </div>
          <div class="story-widget__trust">
            <span>
              <ShieldIcon /> Odpowiedzi sprawdzane z wiedzą firmy
            </span>
            <b>Trust Layer</b>
          </div>
        </header>

        <div class="story-widget__body">
          <div class="story-conversation">
            <AssistantIdentity scenario={scenario} />
            <p class="story-intro">
              Cześć! Znam ofertę {scenario.company} i pomogę Ci znaleźć
              najlepszy następny krok.
            </p>

            <div class="story-user-message story-user-message--first">
              {scenario.firstQuestion}
            </div>

            <div class="story-assistant-turn story-assistant-turn--first">
              <div class="story-assistant-turn__head">
                <AssistantIdentity scenario={scenario} />
                <TypingIndicator />
              </div>
              <p>{scenario.firstAnswer}</p>
            </div>

            <div class="story-user-message story-user-message--second">
              {scenario.secondQuestion}
            </div>

            <div class="story-assistant-turn story-assistant-turn--final">
              <div class="story-assistant-turn__head">
                <AssistantIdentity scenario={scenario} />
                <TypingIndicator />
              </div>
              <p>{scenario.finalAnswer}</p>
              <SceneOutcome id={scenario.id} />
              <div class="story-proof">
                <ShieldIcon /> {scenario.proof}
              </div>
            </div>
          </div>
        </div>

        <div class="story-widget__composer">
          <span class="story-composer-placeholder">Napisz wiadomość...</span>
          <span class="story-composer-draft story-composer-draft--first">
            {scenario.firstQuestion}
          </span>
          <span class="story-composer-draft story-composer-draft--second">
            {scenario.secondQuestion}
          </span>
          <i class="story-composer-send">
            <ArrowUpRightIcon />
          </i>
        </div>
        <div class="story-widget__powered">
          <Mark /> Powered by <strong>Chatland</strong>
        </div>
      </div>
    </>
  );
}

export function HeroDemo() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [sceneVersion, setSceneVersion] = useState(0);
  const [paused, setPaused] = useState(false);
  const activeScenario = scenarios[activeIndex];

  useEffect(() => {
    if (paused || window.matchMedia("(prefers-reduced-motion: reduce)").matches)
      return;
    const interval = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % scenarios.length);
      setSceneVersion((current) => current + 1);
    }, SCENARIO_DURATION_MS);
    return () => window.clearInterval(interval);
  }, [paused]);

  const activateScenario = (index: number) => {
    setActiveIndex(index);
    setSceneVersion((current) => current + 1);
  };

  return (
    <div
      class="hero-story"
      aria-label="Animowany przykład działania Chatland w różnych branżach"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusIn={() => setPaused(true)}
      onFocusOut={() => setPaused(false)}
    >
      <div class="hero-story__toolbar">
        <div class="hero-story__live">
          <i /> Chatland w akcji
        </div>
        <div
          class="hero-story__tabs"
          role="tablist"
          aria-label="Wybierz przykład zastosowania"
        >
          {scenarios.map((scenario, index) => (
            <button
              key={scenario.id}
              type="button"
              role="tab"
              aria-selected={index === activeIndex}
              onClick={() => activateScenario(index)}
            >
              <span>{scenario.step}</span>
              {scenario.tab}
              {index === activeIndex && (
                <i key={`${scenario.id}-${sceneVersion}`} />
              )}
            </button>
          ))}
        </div>
      </div>

      <p class="sr-only" aria-live="polite">
        Przykład: {activeScenario.tab}. Użytkownik:{" "}
        {activeScenario.firstQuestion}
        Asystent: {activeScenario.firstAnswer} Użytkownik:{" "}
        {activeScenario.secondQuestion}
        Asystent: {activeScenario.finalAnswer}
      </p>

      <div
        key={`${activeScenario.id}-${sceneVersion}`}
        class={`story-stage story-stage--${activeScenario.id}`}
        aria-hidden="true"
      >
        <div class="story-site">
          <header class="story-site__head">
            <strong>{activeScenario.company}</strong>
            <div>
              <span>Oferta</span>
              <span>O nas</span>
              <span>Kontakt</span>
            </div>
            <b>PL</b>
          </header>
          <div class="story-site__content">
            <div class="story-site__copy">
              <span>{activeScenario.eyebrow}</span>
              <h3>
                {activeScenario.headline.split("\n").map((line) => (
                  <span key={line}>{line}</span>
                ))}
              </h3>
              <p>{activeScenario.description}</p>
              <b>
                Poznaj ofertę <ArrowUpRightIcon />
              </b>
            </div>
            <SceneArtwork scenario={activeScenario} />
          </div>
          <div class="story-site__footer">
            <span>
              <CheckIcon /> Aktualne informacje
            </span>
            <span>
              <ShieldIcon /> Zweryfikowana firma
            </span>
            <strong>{activeScenario.step} / 03</strong>
          </div>
        </div>

        <StoryWidget scenario={activeScenario} />
      </div>

      <div class="hero-story__caption">
        <span>{activeScenario.company}</span>
        <p>
          <strong>{activeScenario.tab}</strong> · jeden asystent, inny kontekst
          i rezultat
        </p>
        <div>
          {scenarios.map((scenario, index) => (
            <i
              key={scenario.id}
              class={index === activeIndex ? "is-active" : ""}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
